import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import { Spanish } from "flatpickr/dist/l10n/es.js"

export default class extends Controller {
  static targets = [
    "input",
    "calendar",
    "timeInput",
    "timeOptions",
    "timeSelectWrapper",
    "horariosContainer",
    "timeWarning",
    "targetHidden",
    "profesionalHidden"
  ]

  static values = { horariosMap: Object, autoSubmit: Boolean, allowPast: Boolean, excludeTurnoId: Number }

  horariosMapValueChanged() {
    console.log("horariosMapValueChanged keys:", Object.keys(this.horariosMapValue || {}))
    if (!this.picker) return

    this.applyAllowedDays()

    if (this.picker.selectedDates.length > 0) {
      this.generateTimeSlots(this.picker.selectedDates[0], { preselect: this.hasTimeInputTarget ? this.timeInputTarget.value : null })
    }
  }

  connect() {
    this.availableSlots = []
    this.reservedMinutes = new Set()

    const calendarEl = this.hasCalendarTarget ? this.calendarTarget : this.element

    this.initialDateStr = this.hasInputTarget ? (this.inputTarget.value || "") : ""
    this.initialTimeStr = this.hasTimeInputTarget ? (this.timeInputTarget.value || "") : ""

    this.picker = flatpickr(calendarEl, {
      inline: true,
      mode: "single",
      dateFormat: "Y-m-d",
      locale: Spanish,
      weekStartDay: 1,
      minDate: this.allowPastValue ? null : "today",
      onChange: (selectedDates) => {
        const dates = selectedDates.map(d => d.toISOString().slice(0, 10))
        if (this.hasInputTarget) this.inputTarget.value = dates.join(",")
        this.dispatch("selected", { detail: { dates } })

        if (selectedDates.length > 0) {
          this.generateTimeSlots(selectedDates[0])
        } else {
          this.hideTimeSelect()
        }

        try {
          if (this.autoSubmitValue && dates.length > 0) {
            this.updateAdminTable(dates[0])
          }
        } catch (e) {
          console.error("Error auto-updating admin table", e)
        }
      }
    })

    if (this.initialDateStr) {
      const initialDate = this.picker.parseDate(this.initialDateStr, "Y-m-d")
      if (initialDate) this.picker.setDate(initialDate, false)
    }

    if (Object.keys(this.horariosMapValue || {}).length > 0) {
      this.applyAllowedDays()
    }

    const key = this.getSelectedKey()
    if (key) {
      this.loadTargetData(key, { keepSelection: true })
        .then(() => this.applyInitialState())
        .catch(e => console.error("Error initial target load", e))
    }

    if (this.hasTimeInputTarget) {
      this.timeInputTarget.addEventListener("change", () => this.validateTimeRange())
    }

    this.form = this.element.closest("form")
    this.onFormSubmit = this.onFormSubmit.bind(this)
    if (this.form) this.form.addEventListener("submit", this.onFormSubmit)
  }

  disconnect() {
    if (this.form && this.onFormSubmit) {
      this.form.removeEventListener("submit", this.onFormSubmit)
    }
  }

  async targetSelected(event) {
    let key = event?.detail?.value || event?.detail?.id
    if (!key) return

    key = this.normalizeKey(key)

    if (this.hasTargetHiddenTarget) this.targetHiddenTarget.value = key

    if (key.startsWith("prof:") && this.hasProfesionalHiddenTarget) {
      this.profesionalHiddenTarget.value = key.split(":", 2)[1]
    }

    await this.loadTargetData(key)
  }

  async targetChanged(event) {
    let key = event?.target?.value
    if (!key) return
    key = this.normalizeKey(key)
    await this.loadTargetData(key)
  }

  async profesionalSelected(event) {
    let v = event?.detail?.value || (this.hasProfesionalHiddenTarget ? this.profesionalHiddenTarget.value : null)
    if (!v) return

    const key = this.normalizeKey(v)

    if (key.startsWith("prof:") && this.hasProfesionalHiddenTarget) {
      this.profesionalHiddenTarget.value = key.split(":", 2)[1]
    }
    if (this.hasTargetHiddenTarget) this.targetHiddenTarget.value = key

    await this.loadTargetData(key)
  }

  async profesionalChanged(event) {
    let v = event?.target?.value
    if (!v) return

    const key = this.normalizeKey(v)

    if (key.startsWith("prof:") && this.hasProfesionalHiddenTarget) {
      this.profesionalHiddenTarget.value = key.split(":", 2)[1]
    } else if (this.hasProfesionalHiddenTarget) {
      this.profesionalHiddenTarget.value = ""
    }

    if (this.hasTargetHiddenTarget) this.targetHiddenTarget.value = key
    await this.loadTargetData(key)
  }

  applyInitialState() {
    if (!this.picker || !this.initialDateStr) return

    const dateObj = this.picker.parseDate(this.initialDateStr, "Y-m-d")
    if (!dateObj) return

    this.picker.setDate(dateObj, false)
    this.generateTimeSlots(dateObj, { preselect: this.initialTimeStr })
  }

  applyAllowedDays() {
    if (!this.picker) return

    const allowed = Object.keys(this.horariosMapValue || {})
      .map(k => Number(k))
      .filter(n => Number.isInteger(n) && n >= 0 && n <= 6)

    this.picker.set("enable", [])

    if (!allowed.length) {
      this.picker.set("enable", [() => false])
      return
    }

    this.picker.set("enable", [(date) => allowed.includes(date.getDay())])
    this.picker.redraw()
  }

  showTimeSelect() {
    if (!this.hasTimeSelectWrapperTarget) return
    this.timeSelectWrapperTarget.classList.remove("d-none")
    if (this.hasTimeInputTarget) this.timeInputTarget.required = true
  }

  hideTimeSelect() {
    if (!this.hasTimeSelectWrapperTarget) return
    this.timeSelectWrapperTarget.classList.add("d-none")
    if (this.hasTimeInputTarget) {
      this.timeInputTarget.required = false
      this.timeInputTarget.value = ""
      this.timeInputTarget.setCustomValidity("")
      this.timeInputTarget.classList.remove("is-invalid")
      this.populateTimeOptions([])
    }
    this.clearTimeWarning()
    this.availableSlots = []
  }

  async fetchReservedMinutes(key, dateStr) {
    const params = new URLSearchParams({ fecha: dateStr })

    if (this.hasExcludeTurnoIdValue && this.excludeTurnoIdValue > 0) {
      params.set("exclude_turno_id", String(this.excludeTurnoIdValue))
    }

    if (key.startsWith("prof:")) {
      params.set("profesional_id", key.split(":", 2)[1])
    } else if (key.startsWith("srv:")) {
      params.set("servicio_id", key.split(":", 2)[1])
    } else {
      return new Set()
    }

    try {
      const resp = await fetch(`/turnero/turnos/horarios_reservados?${params.toString()}`)
      if (!resp.ok) return new Set()

      const json = await resp.json()
      return new Set(json.reservados || [])
    } catch (e) {
      console.error("Error fetching reserved times", e)
      return new Set()
    }
  }

  slotIsAvailable(slotStart, reserved, preselect) {
    if (slotStart === preselect) return true
    return !reserved.has(slotStart)
  }

  populateTimeOptions(available) {
    if (!this.hasTimeOptionsTarget) return

    const datalist = this.timeOptionsTarget
    datalist.innerHTML = ""

    available.forEach((hhmm) => {
      const option = document.createElement("option")
      option.value = hhmm
      datalist.appendChild(option)
    })
  }

  refreshTimeSuggestions(rawValue = null) {
    if (!this.hasTimeInputTarget) return
    const value = rawValue == null ? this.timeInputTarget.value : rawValue
    const candidates = this.matchSlotsByPrefix(value)
    this.populateTimeOptions(candidates)
  }

  async generateTimeSlots(date, options = {}) {
    const dayOfWeek = date.getDay() // 0..6
    const ranges = this.horariosMapValue ? this.horariosMapValue[dayOfWeek] : null

    if (!ranges || ranges.length === 0) {
      this.hideTimeSelect()
      if (this.hasHorariosContainerTarget) {
        this.horariosContainerTarget.innerHTML = "<p class='text-danger mb-0'>No hay horarios disponibles para este día.</p>"
      }
      return
    }

    const key = this.getSelectedKey()
    if (!key) {
      this.hideTimeSelect()
      return
    }

    const dateStr = this.formatLocalDate(date)
    const preselect = options.preselect || (this.hasTimeInputTarget ? this.timeInputTarget.value : null)

    const reserved = await this.fetchReservedMinutes(key, dateStr)
    this.reservedMinutes = reserved

    const available = []

    ranges.forEach(range => {
      const start = this.hhmmToMinutes(range.inicio)
      const end = this.hhmmToMinutes(range.fin)
      if (start == null || end == null) return

      const step = 15
      for (let m = start; (m + step) <= end; m += step) {
        const hourString = this.minutesToHHMM(m)
        if (this.slotIsAvailable(hourString, reserved, preselect)) {
          available.push(hourString)
        }
      }
    })

    this.availableSlots = available

    if (available.length === 0) {
      this.hideTimeSelect()
      if (this.hasHorariosContainerTarget) {
        this.horariosContainerTarget.innerHTML = "<p class='text-danger mb-0'>No hay horarios disponibles para este día.</p>"
      }
      return
    }

    this.showTimeSelect()
    this.populateTimeOptions(available)

    if (this.hasTimeInputTarget) {
      const current = this.timeInputTarget.value || preselect || ""
      this.timeInputTarget.value = current && available.includes(current) ? current : ""
      this.refreshTimeSuggestions(this.timeInputTarget.value)
    }

    if (this.hasHorariosContainerTarget) {
      this.horariosContainerTarget.innerHTML = `<p class='text-muted mb-0'>${available.length} horario${available.length === 1 ? "" : "s"} disponible${available.length === 1 ? "" : "s"}</p>`
    }

    this.validateTimeRange()
  }

  async updateAdminTable(dateStr) {
    try {
      if (this.hasInputTarget) this.inputTarget.value = dateStr
      const form = this.element.closest("form")
      if (!form) return
      form.requestSubmit()
    } catch (e) {
      console.error("Error en updateAdminTable", e)
    }
  }

  async loadTargetData(key, opts = {}) {
    const keepSelection = opts.keepSelection

    try {
      if (!keepSelection) {
        if (this.picker) this.picker.clear()
        if (this.hasInputTarget) this.inputTarget.value = ""
        this.hideTimeSelect()
        if (this.hasHorariosContainerTarget) this.horariosContainerTarget.innerHTML = ""
        this.initialDateStr = ""
        this.initialTimeStr = ""
      }

      // PROFESIONAL
      if (key.startsWith("prof:")) {
        const matricula = key.split(":", 2)[1]
        if (this.hasProfesionalHiddenTarget) this.profesionalHiddenTarget.value = matricula
        await this.loadProfesionalData(matricula)
        return
      }

      // SERVICIO
      if (key.startsWith("srv:")) {
        const serviceId = key.split(":", 2)[1]
        if (this.hasProfesionalHiddenTarget) this.profesionalHiddenTarget.value = ""
        await this.loadServicioData(serviceId)
        return
      }

      // fallback
      this.horariosMapValue = {}
    } catch (e) {
      console.error("Error loading target horarios", e)
      this.horariosMapValue = {}
    }
  }

  async loadProfesionalData(matricula) {
    try {
      const resp = await fetch(`/profesionales/${encodeURIComponent(matricula)}/agenda_info`, {
        headers: { "Accept": "application/json" }
      })
      const data = resp.ok ? await resp.json() : { horarios_map: {} }

      const rawMap = data.horarios_map || {}
      const newMap = {}
      Object.keys(rawMap).forEach(k => { newMap[Number(k)] = rawMap[k] })

      this.horariosMapValue = newMap
    } catch (e) {
      console.error("Error loading agenda profesional", e)
      this.horariosMapValue = {}
    }
  }

  async loadServicioData(serviceId) {
    try {
      const resp = await fetch(`/turnero/servicios/${encodeURIComponent(serviceId)}/agenda_info`, {
        headers: { "Accept": "application/json" }
      })
      const data = resp.ok ? await resp.json() : { horarios_map: {} }

      const rawMap = data.horarios_map || {}
      const newMap = {}
      Object.keys(rawMap).forEach(k => { newMap[Number(k)] = rawMap[k] })

      this.horariosMapValue = newMap
    } catch (e) {
      console.error("Error loading agenda servicio", e)
      this.horariosMapValue = {}
    }
  }

  validateTimeRange(options = {}) {
    const showRequired = options.showRequired === true
    const showMessages = options.showMessages !== false

    this.clearTimeWarning()

    if (!this.hasTimeInputTarget || !this.timeInputTarget.value) {
      if (this.hasTimeInputTarget) {
        if (showRequired) {
          this.timeInputTarget.setCustomValidity("Debe completar una hora en formato HH:MM")
          this.timeInputTarget.classList.add("is-invalid")
          if (showMessages) this.showTimeWarning("⚠️ Debe completar una hora en formato HH:MM.")
        } else {
          this.timeInputTarget.setCustomValidity("")
          this.timeInputTarget.classList.remove("is-invalid")
        }
      }
      return
    }

    const selectedTime = (this.timeInputTarget.value || "").trim()

    if (this.isPartialTime(selectedTime)) {
      if (showRequired) {
        this.timeInputTarget.setCustomValidity("Debe completar una hora válida en formato HH:MM")
        this.timeInputTarget.classList.add("is-invalid")
        if (showMessages) this.showTimeWarning("⚠️ Debe completar una hora válida en formato HH:MM.")
      } else {
        this.timeInputTarget.setCustomValidity("")
        this.timeInputTarget.classList.remove("is-invalid")
      }
      return
    }

    if (!this.isHHMM(selectedTime)) {
      this.timeInputTarget.setCustomValidity("Formato de hora inválido. Use HH:MM")
      this.timeInputTarget.classList.add("is-invalid")
      if (showMessages) this.showTimeWarning("⚠️ Formato de hora inválido. Use HH:MM (por ejemplo 15:30).")
      return
    }

    this.timeInputTarget.setCustomValidity("")
    this.timeInputTarget.classList.remove("is-invalid")

    if (this.availableSlots.length > 0 && !this.availableSlots.includes(selectedTime)) {
      this.timeInputTarget.setCustomValidity("El horario no está disponible")
      this.timeInputTarget.classList.add("is-invalid")
      if (showMessages) this.showTimeWarning("⚠️ El horario seleccionado no está disponible.")
      return
    }

    if (!this.picker || !this.picker.selectedDates || this.picker.selectedDates.length === 0) return

    const selectedDate = this.picker.selectedDates[0]
    const dayOfWeek = selectedDate.getDay()

    const ranges = this.horariosMapValue ? this.horariosMapValue[dayOfWeek] : null
    if (!ranges || ranges.length === 0) return

    let isWithinRange = false
    let minTime = null
    let maxTime = null

    ranges.forEach(range => {
      const rangeStart = range.inicio
      const rangeEnd = range.fin

      if (selectedTime >= rangeStart && selectedTime <= rangeEnd) isWithinRange = true
      if (!minTime || rangeStart < minTime) minTime = rangeStart
      if (!maxTime || rangeEnd > maxTime) maxTime = rangeEnd
    })

    if (!isWithinRange) {
      this.timeInputTarget.setCustomValidity("El horario está fuera del rango permitido")
      this.timeInputTarget.classList.add("is-invalid")
      const message = `⚠️ El horario debe estar entre ${minTime} y ${maxTime}`
      if (showMessages) this.showTimeWarning(message)
      return
    }

    this.timeInputTarget.setCustomValidity("")
    this.timeInputTarget.classList.remove("is-invalid")
  }

  timeSelected(event) {
    const eventType = event?.type

    if (eventType === "input" && this.hasTimeInputTarget) {
      this.sanitizeTimeInput()
      this.refreshTimeSuggestions(this.timeInputTarget.value)
    }

    if (eventType === "blur") {
      this.autocompletePartialTime()
      this.refreshTimeSuggestions(this.timeInputTarget.value)
      this.validateTimeRange({ showRequired: true, showMessages: true })
      return
    }

    if (eventType === "change") {
      this.validateTimeRange({ showRequired: true, showMessages: true })
      return
    }

    this.validateTimeRange({ showRequired: false, showMessages: false })
  }

  onFormSubmit(event) {
    if (!this.hasTimeInputTarget || !this.hasTimeSelectWrapperTarget) return
    if (this.timeSelectWrapperTarget.classList.contains("d-none")) return

    this.autocompletePartialTime()
    this.validateTimeRange({ showRequired: true, showMessages: true })

    if (!this.timeInputTarget.checkValidity()) {
      event.preventDefault()
      this.timeInputTarget.reportValidity()
    }
  }

  autocompletePartialTime() {
    if (!this.hasTimeInputTarget) return

    const raw = (this.timeInputTarget.value || "").trim()
    if (!raw || !this.availableSlots.length) return

    if (this.isHHMM(raw)) return

    const candidates = this.matchSlotsByPrefix(raw)
    if (!candidates.length) return

    let selected = candidates[0]
    const hourOnly = raw.match(/^\d{1,2}$/)
    if (hourOnly) {
      const hh = String(parseInt(raw, 10)).padStart(2, "0")
      const exactHour = `${hh}:00`
      if (candidates.includes(exactHour)) selected = exactHour
    }

    this.timeInputTarget.value = selected
  }

  matchSlotsByPrefix(prefix) {
    const value = String(prefix || "").trim()
    if (!value) return this.availableSlots

    if (/^\d{1,2}$/.test(value)) {
      const hh = String(parseInt(value, 10)).padStart(2, "0")
      return this.availableSlots.filter((slot) => slot.startsWith(`${hh}:`))
    }

    if (/^\d{1,2}:$/.test(value)) {
      const hh = String(parseInt(value.replace(":", ""), 10)).padStart(2, "0")
      return this.availableSlots.filter((slot) => slot.startsWith(`${hh}:`))
    }

    if (/^\d{1,2}:\d{1,2}$/.test(value)) {
      const [hRaw, mRaw] = value.split(":")
      const hh = String(parseInt(hRaw, 10)).padStart(2, "0")
      const mmPrefix = mRaw
      return this.availableSlots.filter((slot) => slot.startsWith(`${hh}:${mmPrefix}`))
    }

    return []
  }

  sanitizeTimeInput() {
    if (!this.hasTimeInputTarget) return

    const raw = this.timeInputTarget.value || ""
    let value = raw.replace(/[^\d:]/g, "")

    const firstColon = value.indexOf(":")
    if (firstColon !== -1) {
      const hh = value.slice(0, firstColon).replace(/:/g, "").slice(0, 2)
      const mm = value.slice(firstColon + 1).replace(/:/g, "").slice(0, 2)
      value = `${hh}:${mm}`
    } else {
      value = value.slice(0, 2)
    }

    if (value !== raw) this.timeInputTarget.value = value
  }

  timeKeydown(event) {
    if (!this.hasTimeInputTarget) return

    const key = event.key
    const isDigit = /^\d$/.test(key)
    const navigationKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End", "Enter"]

    if (event.ctrlKey || event.metaKey || event.altKey) return
    if (navigationKeys.includes(key)) return

    if (key === ":") {
      if (this.timeInputTarget.value.includes(":")) {
        event.preventDefault()
      }
      return
    }

    if (!isDigit) {
      event.preventDefault()
    }
  }

  clearTimeWarning() {
    if (!this.hasTimeWarningTarget) return
    this.timeWarningTarget.innerHTML = ""
    this.timeWarningTarget.classList.add("d-none")
    this.timeWarningTarget.classList.remove("text-warning", "mt-2")
  }

  showTimeWarning(message) {
    if (!this.hasTimeWarningTarget) return
    this.timeWarningTarget.innerHTML = message
    this.timeWarningTarget.classList.remove("d-none")
    this.timeWarningTarget.classList.add("text-warning", "mt-2")
  }

  // =========================
  // Helpers
  // =========================

  normalizeKey(v) {
    const s = String(v || "").trim()
    if (!s) return ""

    if (s.startsWith("prof:") || s.startsWith("srv:")) return s

    if (/^\d+$/.test(s)) return `prof:${s}`

    return s
  }

  getSelectedKey() {
    if (this.hasTargetHiddenTarget && this.targetHiddenTarget.value) {
      return this.normalizeKey(this.targetHiddenTarget.value)
    }

    if (this.hasProfesionalHiddenTarget && this.profesionalHiddenTarget.value) {
      return this.normalizeKey(this.profesionalHiddenTarget.value)
    }

    return null
  }

  hhmmToMinutes(hhmm) {
    const s = (hhmm || "").toString().trim()
    if (!s) return null
    const [h, m] = s.split(":").map(v => parseInt(v, 10))
    if (Number.isNaN(h) || Number.isNaN(m)) return null
    return h * 60 + m
  }

  isPartialTime(value) {
    return /^\d{1,2}$/.test(value) || /^\d{1,2}:\d{0,1}$/.test(value)
  }

  isHHMM(value) {
    return /^([01]\d|2[0-3]):[0-5]\d$/.test(value)
  }

  minutesToHHMM(mins) {
    const h = Math.floor(mins / 60)
    const m = mins % 60
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
  }

  formatLocalDate(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }
}